import { useStore } from 'effector-react';
import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import Badge from 'components/Badge';
import Icon from 'components/Icon';
import Preloader from 'components/Preloader';
import { TabLink, TabPanel, Tabs } from 'components/Tabs';
import { Typo } from 'components/typography/Typo';
import { dataStoragesSteps, noSQLDatabasesSteps } from 'layouts/AuthorizedWithLeftMenu/Breadcrumbs';
import Header from 'layouts/AuthorizedWithLeftMenu/Header';
import { getNoSQLDatabaseDetails } from 'models/noSQLDatabases/api';
import { NoSQLDatabaseDetails } from 'models/noSQLDatabases/dto';
import { noSQLDatabaseInstancesById } from 'models/noSQLDatabases/store';
import { toLocaleString } from 'services/numbers';
import { PATHS } from 'services/router';
import { AutomaticDiscoveryTooltip } from 'views/common/AutomaticDiscovery';
import styles from '../index.module.css';
import { CollectionsTable } from './CollectionsTable';
import { Summary } from './Summary';

function DatabaseError() {
	const params: {
		id: string;
	} = useParams();

	return (
		<>
			<Header>
				<Header.Breadcrumbs steps={dataStoragesSteps} finalStep="Not found" />

				<Header.Title>Error has occurred while loading database info</Header.Title>
			</Header>

			<Typo variant="D/Regular/Body-S" color="secondary">
				Database with id={params.id} not found or was deleted.
			</Typo>
		</>
	);
}

function NoSQLDatabaseItemWrapper() {
	const [db, setDB] = useState<NoSQLDatabaseDetails | null>(null);
	const [loading, setLoading] = useState(true);

	const params: { id: string } = useParams();

	useEffect(() => {
		getNoSQLDatabaseDetails(Number(params.id))
			.then(setDB)
			.finally(() => setLoading(false));
	}, [params.id]);

	return (
		<Preloader isLoading={loading}>
			{db ? <NoSQLDatabaseItem db={db} /> : <DatabaseError />}
		</Preloader>
	);
}

const tabLabels = {
	summary: 'Summary',
	collections: 'Collections',
};

interface NoSQLDatabaseItemProps {
	db: NoSQLDatabaseDetails;
}

function NoSQLDatabaseItem({ db }: Readonly<NoSQLDatabaseItemProps>) {
	const history = useHistory();
	const params: {
		tab?: 'summary';
	} = useParams();
	const databaseInstances = useStore(noSQLDatabaseInstancesById);
	const instance = useMemo(
		() => databaseInstances[db.instance_id],
		[db.instance_id, databaseInstances]
	);

	const tabLinks = useMemo(
		() => ({
			summary: generatePath(PATHS.NOSQL_DATABASES_ITEM, {
				id: db.id,
				tab: 'summary',
			}),
			collections: generatePath(PATHS.NOSQL_DATABASES_ITEM, {
				id: db.id,
				tab: 'collections',
			}),
		}),
		[db.id]
	);

	// Set default tab, if not specified.
	useEffect(function () {
		if (!params.tab) {
			const path = generatePath(PATHS.NOSQL_DATABASES_ITEM, {
				...params,
				tab: 'summary',
			});
			history.replace(path + history.location.search);
		}
	}, []);

	const isScanned = !!db.last_checked;

	const steps = [
		...noSQLDatabasesSteps,
		{
			path: generatePath(PATHS.NOSQL_DATABASES_ITEM, {
				id: db.id,
				tab: 'summary',
			}),
			name: db.name,
		},
	];

	return (
		<>
			<Header>
				<Header.Breadcrumbs steps={steps} finalStep={tabLabels[params.tab ?? 'summary']} />

				<Header.Title>
					<Typo variant="D/Medium/H400-Page-Title" className={styles.title}>
						{!instance.is_crawler_attached && <Icon name="Radar/Filled" size={24} />}
						{db.name}
					</Typo>
				</Header.Title>

				<Header.SensitiveSwitcher />
			</Header>

			<Tabs value={tabLinks[params.tab ?? 'summary']}>
				<TabLink value={tabLinks['summary']} label={tabLabels['summary']} />

				{instance.is_crawler_attached ? (
					<TabLink
						value={tabLinks['collections']}
						label={
							<>
								{tabLabels['collections']}
								<Badge
									badgeContent={isScanned ? toLocaleString(db.collections_count) : 'N/A'}
									data-test="database-tables-count"
								/>
							</>
						}
					/>
				) : (
					<AutomaticDiscoveryTooltip>
						<TabLink
							disabled
							value={tabLinks['collections']}
							label={
								<>
									{tabLabels['collections']}
									<Badge badgeContent="N/A" data-test="database-tables-count" />
								</>
							}
						/>
					</AutomaticDiscoveryTooltip>
				)}
			</Tabs>

			{/* TABS CONTENT */}
			<TabPanel value={params.tab} index="summary">
				<Summary db={db} instance={instance} />
			</TabPanel>

			<TabPanel value={params.tab} index="collections">
				<CollectionsTable databaseId={db.id} />
			</TabPanel>
		</>
	);
}

export { NoSQLDatabaseItemWrapper as NoSQLDatabaseItem };
